import React, { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { FiDownload } from "react-icons/fi";
import { IoClose } from "react-icons/io5";
import { floorplannerStore } from "../../store/floorplannerStore";
import { renderStore } from "../../store/renderStore";
import { Tooltip } from "flowbite-react";
import { PiMinus, PiPlus } from "react-icons/pi";
import { observer } from "mobx-react-lite";
import { editorStore } from "../../store/editorStore";
import Clarity from "@microsoft/clarity";
import { capturePosthogEvent } from "../../utils/posthog";
import { useExportPreviewSnapshot } from "../../hooks/useExportPreviewSnapshot";
import { useExportPreviewState } from "../../hooks/useExportPreviewState";
import {
  buildExportAnnotationOptions,
  getExportTitleBlockFrame,
  renderTitleBlockPreviewImage,
} from "../../utils/exportTitleBlock";
import { EXPORT_FORMAT_OPTIONS, EXPORT_SCALE_PRESETS, ExportFormatId, ScalePreset } from "../../utils/exportFormats";

interface DropdownProps {
  position: { top: number; left: number };
  onClose: () => void;
  dropdownRef: React.Ref<HTMLDivElement>;
}

export const DownloadProject: React.FC<DropdownProps> = observer(({ onClose, dropdownRef }) => {
  const { t } = useTranslation();
  const {
    selectedFormatId,
    selectedFormat,
    setFormatId,
    zoom,
    pan,
    previewSize,
    drawingPreviewSize,
    drawablePixelSize,
    captureMetrics,
    resolvedScale,
    selectedScalePreset,
    handleWheelZoom,
    handleZoomStep,
    handlePan,
    setScalePreset,
  } = useExportPreviewState({
    defaultFormatId: "a4-portrait",
  });
  const [isInitializing, setIsInitializing] = useState(true);
  const [includeTitleBlock, setIncludeTitleBlock] = useState(true);
  const showScale = selectedFormat.supportsScale && !editorStore.view3D;

  const imgRef = useRef<HTMLDivElement>(null);

  useExportPreviewSnapshot({
    selectedFormatId,
    selectedFormat,
    drawablePixelSize,
    setIsInitializing,
  });

  const handleDownload = async (event: React.FormEvent) => {
    event.preventDefault();
    const target = event.currentTarget as typeof event.currentTarget & {
      format: HTMLSelectElement;
    };
    const fileType = target.format.value;

    renderStore.setRendering(true);

    try {
      if (editorStore.camera) {
        let operation = "downloadPNG";
        if (fileType === "pdf") {
          operation = "downloadPDF";
        } else if (fileType === "jpg") {
          operation = "downloadJPG";
        }

        Clarity.event("download_project");
        capturePosthogEvent("download_project");
        const target = {
          orientation: selectedFormat.orientation,
          format: selectedFormat.format,
          width: selectedFormat.width,
          height: selectedFormat.height,
        };
        const annotationOptions = buildExportAnnotationOptions(
          selectedFormat,
          resolvedScale,
          floorplannerStore.name,
          floorplannerStore.updatedAt,
          includeTitleBlock,
          showScale,
        );

        if (editorStore.view3D) {
          await renderStore.capture3DSnapshot(operation, target, editorStore.camera, annotationOptions, {
            pan,
            zoom,
            previewSize,
          });
        } else {
          await renderStore.captureSnapshot(
            operation,
            target,
            captureMetrics?.captureBox,
            editorStore.camera,
            annotationOptions,
          );
        }
      }
    } finally {
      renderStore.setRendering(false);
      onClose();
    }
  };

  const annotationOptions = buildExportAnnotationOptions(
    selectedFormat,
    resolvedScale,
    floorplannerStore.name,
    floorplannerStore.updatedAt,
    includeTitleBlock,
    showScale,
  );
  // Compute frame at full export resolution, then scale to preview size so proportions match the final render exactly.
  const previewScaleFactor = drawablePixelSize.width > 0 ? previewSize.width / drawablePixelSize.width : 1;
  const exportFrame = annotationOptions
    ? getExportTitleBlockFrame(drawablePixelSize.width, drawablePixelSize.height, selectedFormat)
    : null;
  const titleBlockFrame = exportFrame
    ? {
        width: exportFrame.width * previewScaleFactor,
        height: exportFrame.height * previewScaleFactor,
        marginRight: exportFrame.marginRight * previewScaleFactor,
        marginBottom: exportFrame.marginBottom * previewScaleFactor,
      }
    : null;

  const titleBlockImageSrc = useMemo(() => {
    if (!annotationOptions || !titleBlockFrame) return null;
    return renderTitleBlockPreviewImage(
      previewSize.width,
      previewSize.height,
      annotationOptions.titleBlock,
      annotationOptions.format,
      titleBlockFrame,
    );
  }, [
    annotationOptions?.titleBlock.projectName,
    annotationOptions?.titleBlock.formatLabel,
    annotationOptions?.titleBlock.scaleLabel,
    annotationOptions?.titleBlock.updatedAtLabel,
    titleBlockFrame?.width,
    titleBlockFrame?.height,
    titleBlockFrame?.marginRight,
    titleBlockFrame?.marginBottom,
    previewSize.width,
    previewSize.height,
  ]);

  return (
    <div
      className="absolute z-50 w-[600px] overflow-y-auto rounded border border-gray-300 bg-[#EDEBE6] shadow-md top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2"
      ref={dropdownRef}
      onWheel={(event) => event.stopPropagation()}
    >
      <div className="flex flex-cols items-center justify-between p-2">
        <div className="text-sm font-normal">{t("download.title")}</div>
        <div onClick={onClose} className="cursor-pointer text-sm text-gray-500">
          <IoClose style={{ strokeWidth: 0.5 }} size={24} />
        </div>
      </div>
      <div className="w-full border-b border-gray-300" />

      <div className="mt-2 flex w-full flex-col items-center justify-between px-10">
        <div className="w-full text-md font-normal">{t("common.selectArea")}</div>
        <div className="w-full text-sm text-gray-500">{t("common.zoomAreaDownloadHint")}</div>

        {isInitializing ? (
          <div className="flex w-full items-center justify-center p-8" style={{ height: `${previewSize.height}px` }}>
            <div className="mr-3 h-8 w-8 animate-spin rounded-full border-b-2 border-gray-600" />
            <span className="text-gray-600">{t("common.initializingPreview")}</span>
          </div>
        ) : (
          <div className="mt-2 flex w-full select-none flex-row items-center justify-center">
            <div className="rounded-sm mt-4 overflow-hidden rounded border border-gray-300 bg-white shadow-md">
              <div
                className="relative overflow-hidden bg-white"
                style={{
                  cursor: "grab",
                  width: `${previewSize.width}px`,
                  height: `${previewSize.height}px`,
                }}
                onWheel={handleWheelZoom}
                onMouseMove={handlePan}
              >
                <div
                  className="absolute select-none"
                  ref={imgRef}
                  style={{
                    transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
                    transformOrigin: "center center",
                    width: drawingPreviewSize.width,
                    height: previewSize.height,
                  }}
                >
                  <img
                    src={renderStore.imageData}
                    alt={floorplannerStore.name}
                    className="h-full w-full object-contain"
                    draggable={false}
                  />
                </div>
                {titleBlockFrame && titleBlockImageSrc && (
                  <img
                    src={titleBlockImageSrc}
                    alt=""
                    className="pointer-events-none absolute"
                    draggable={false}
                    style={{
                      right: `${titleBlockFrame.marginRight}px`,
                      bottom: `${titleBlockFrame.marginBottom}px`,
                      width: `${titleBlockFrame.width}px`,
                      height: `${titleBlockFrame.height}px`,
                    }}
                  />
                )}
              </div>
            </div>

            <div className="ml-2 flex flex-col space-y-2">
              <Tooltip content={t("common.zoomIn")} placement="right">
                <button
                  onClick={() => handleZoomStep(0.1)}
                  className="w-full rounded-sm border border-gray-300 bg-white p-2 text-center shadow-md hover:bg-gray-100"
                >
                  <PiPlus size={20} />
                </button>
              </Tooltip>
              <Tooltip content={t("common.zoomOut")} placement="right">
                <button
                  onClick={() => handleZoomStep(-0.1)}
                  className="w-full rounded-sm border border-gray-300 bg-white p-2 text-center shadow-md hover:bg-gray-100"
                >
                  <PiMinus size={20} />
                </button>
              </Tooltip>
            </div>
          </div>
        )}
      </div>

      <form className="flex h-full w-full flex-col items-center justify-between px-10" onSubmit={handleDownload}>
        <div className="flex flex-cols items-start justify-between p-2 w-full gap-4">
          <div className="w-60 text-sm font-normal">
            <label htmlFor="format">{t("common.fileType")}</label>
            <br />
            <select id="format" name="format" className="mt-2 w-full">
              <option value="pdf">{t("common.pdf")}</option>
              <option value="png">{t("common.png")}</option>
              <option value="jpg">{t("common.jpg")}</option>
            </select>
          </div>
          <div className="w-60 text-sm text-gray-500">
            <label htmlFor="resolution">{t("common.format")}</label>
            <br />
            <select
              id="resolution"
              name="resolution"
              className="mt-2 w-full"
              value={selectedFormatId}
              onChange={(event) => setFormatId(event.target.value as ExportFormatId)}
            >
              {EXPORT_FORMAT_OPTIONS.filter((format) => format.supportsScale).map((format) => (
                <option key={format.id} value={format.id}>
                  {format.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {showScale && (
          <div className="w-full px-2 pb-2 text-sm text-gray-500">
            <label htmlFor="scale">{t("common.scale")}</label>
            <br />
            <select
              id="scale"
              name="scale"
              className="mt-2 w-full"
              value={selectedScalePreset.toString()}
              onChange={(event) => {
                const value = event.target.value;
                setScalePreset(value === "custom" ? "custom" : (Number(value) as ScalePreset));
              }}
            >
              <option value="custom">{t("common.custom")}</option>
              {EXPORT_SCALE_PRESETS.map((preset) => (
                <option key={preset} value={preset.toString()}>
                  {`1:${preset}`}
                </option>
              ))}
            </select>
          </div>
        )}

        {selectedFormat.supportsScale && (
          <div className="w-full px-2 pb-2">
            <label htmlFor="download-include-title-block" className="flex cursor-pointer items-center gap-3 text-sm text-gray-900">
              <input
                id="download-include-title-block"
                type="checkbox"
                checked={includeTitleBlock}
                onChange={(event) => setIncludeTitleBlock(event.target.checked)}
                className="h-5 w-5 rounded-sm border border-gray-400 bg-white text-black focus:ring-0"
              />
              <span>{t("download.includeInfoBox")}</span>
            </label>
          </div>
        )}

        <button
          id="capture"
          type="submit"
          className={
            renderStore.rendering
              ? "mb-4 mt-4 w-full cursor-not-allowed rounded-md bg-gray-600 p-4 text-white"
              : "mb-4 mt-4 w-full rounded-md bg-black p-4 text-white"
          }
          disabled={renderStore.rendering}
        >
          <div className="flex flex-row items-center justify-center">
            {renderStore.rendering && <div className="mr-3 h-5 w-5 animate-spin rounded-full border-b-2 border-white" />}
            {!renderStore.rendering && <>{t("common.downloadNamed", { name: floorplannerStore.name })}</>}
            &nbsp; <FiDownload className="text-xl transform scale-90" />
          </div>
        </button>
      </form>
    </div>
  );
});
